import { useRef, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Stack, Switch, Typography,
} from '@mui/material'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { ChecklistItemType, ChecklistTemplateSummary } from '@cmms/types'

import { primary } from '../../theme/palette'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'

// Mirrors AssetsImportDialog UX (template download, dropzone, parse,
// validation, import + results) so the in-app import pattern stays
// consistent across modules. The big difference: a checklist is nested,
// so a CSV row = one item, and we group rows by template_name + section.

const VALID_TYPES: ChecklistItemType[] = [
  'text', 'number', 'yes_no', 'pass_fail',
  'multiple_choice', 'checkbox', 'date', 'rating', 'instruction',
]

const TYPES_REQUIRING_OPTIONS: ChecklistItemType[] = ['multiple_choice', 'checkbox']

const SAMPLE_CSV = `template_name,template_description,section_title,item_label,item_type,required,item_description,options,weight,correct_answer,option_points,min_value,max_value
HVAC Quarterly PM,Quarterly preventive maintenance for HVAC units,Visual Inspection,Check belt tension,pass_fail,true,,,10,pass,,,
HVAC Quarterly PM,,Visual Inspection,Note any visible leaks,text,false,,,,,,,
HVAC Quarterly PM,,Visual Inspection,Filter condition,multiple_choice,true,Pick the closest match,Clean|Light dust|Dirty|Replace,10,,10|5|0|0,,
HVAC Quarterly PM,,Operations,Compressor amps (A),number,true,Read from clamp meter,,5,,,10,25
HVAC Quarterly PM,,Operations,Supply air temp (°C),number,true,,,,,,,
HVAC Quarterly PM,,Operations,System running normally,yes_no,true,,,10,yes,,,
Boiler Monthly Check,Monthly boiler safety check,Pressure,System pressure (PSI),number,true,Read from main gauge,,5,,,80,120
Boiler Monthly Check,,Pressure,Pressure gauge calibrated,yes_no,true,,,10,yes,,,
Boiler Monthly Check,,Safety,Emergency shutoff tested,pass_fail,true,Run a manual trip,,10,pass,,,`

interface ImportError {
  row: number
  field: string
  value: string
  message: string
}

interface ParsedItem {
  rowNum: number
  label: string
  type: ChecklistItemType
  required: boolean
  description?: string
  options?: string[]
  /// Scoring — see ChecklistBuilder's ItemRow for the same semantics.
  /// All optional; a blank/zero weight means "not scored".
  weight?: number
  correctAnswer?: string
  optionPoints?: Record<string, number>
  minValue?: number
  maxValue?: number
}

interface ParsedSection {
  title: string
  items: ParsedItem[]
}

interface ParsedTemplate {
  name: string
  description?: string
  sections: ParsedSection[]
}

interface Props {
  open: boolean
  onClose: () => void
}

export function ChecklistsImportDialog({ open, onClose }: Props) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)

  const [file, setFile]     = useState<File | null>(null)
  const [parsed, setParsed] = useState<ParsedTemplate[] | null>(null)
  const [errors, setErrors] = useState<ImportError[]>([])
  const [importing, setImporting] = useState(false)
  const [progress, setProgress]   = useState<{ template: number; total: number; name: string } | null>(null)
  const [result, setResult]       = useState<{
    createdTemplates: number; appendedTemplates: number; newVersions: number;
    sections: number; items: number; failedTemplates: number
  } | null>(null)

  // When ON (default), a CSV template whose name matches an existing one
  // appends sections/items rather than creating a duplicate. Published
  // existing templates are immutable, so we transparently call newVersion
  // first to clone them into a fresh draft we can mutate.
  const [appendMode, setAppendMode] = useState(true)

  // Existing templates list, looked up once per file-pick so the
  // summary card can show "X new, Y will append" before importing.
  const [existing, setExisting] = useState<ChecklistTemplateSummary[]>([])

  function downloadSample() {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'checklists_import_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    setResult(null)
    setErrors([])
    setParsed(null)
    const reader = new FileReader()
    reader.onload = async (ev) => {
      const text = ev.target?.result as string
      const { templates, errors: parseErrors } = parseCSV(text)
      setParsed(templates)
      setErrors(parseErrors)

      // Pull every checklist (any status) so the summary can show which
      // CSV templates collide with existing ones. Done after parse so the
      // user sees row-level errors immediately if the CSV is malformed.
      try {
        const list = await api.checklists.list({})
        setExisting(Array.isArray(list) ? list : [])
      } catch {
        // Non-fatal — appendMode just won't have anything to match.
        setExisting([])
      }
    }
    reader.readAsText(f)
  }

  async function handleImport() {
    if (!parsed || parsed.length === 0) return
    setImporting(true)

    const runErrors: ImportError[] = []
    let createdTemplates   = 0  // brand-new templates
    let appendedTemplates  = 0  // existing draft mutated in place
    let newVersions        = 0  // existing published → new-version draft
    let createdSections    = 0
    let createdItems       = 0
    let failedTemplates    = 0

    for (let t = 0; t < parsed.length; t++) {
      const tpl = parsed[t]!
      setProgress({ template: t + 1, total: parsed.length, name: tpl.name })

      // Decide how to land this template.
      const plan = appendMode
        ? planForTemplate(tpl, existing)
        : { mode: 'create' as const }

      let templateId:        string
      let existingSections:  { id: string; title: string }[] = []
      let existingItemCount  = 0   // for the section's position offset on append

      try {
        if (plan.mode === 'create') {
          const created = await api.checklists.create({
            name: tpl.name,
            description: tpl.description,
          })
          templateId = created.id
          createdTemplates++
        } else {
          // 'append-draft' or 'append-via-new-version' — resolve to a
          // mutable draft templateId either way, then load its current
          // sections so we can append-by-title.
          let baseId = plan.existingId
          if (plan.mode === 'append-via-new-version') {
            const newVer = await api.checklists.newVersion(plan.existingId)
            baseId = newVer.id
            newVersions++
          } else {
            appendedTemplates++
          }
          const full = await api.checklists.get(baseId)
          templateId       = full.id
          existingSections = full.sections.map((s) => ({ id: s.id, title: s.title }))
        }
      } catch (e) {
        failedTemplates++
        const msg = e instanceof ApiError ? e.message : 'Unknown error'
        runErrors.push({ row: 0, field: 'template', value: tpl.name,
          message: `Failed to open template "${tpl.name}": ${msg}` })
        continue
      }

      // Walk the parsed sections, reusing matching ones if appending,
      // otherwise creating fresh. Section positions appended after the
      // existing ones (so the order in the UI is "existing first, then
      // imported"); item positions same — appended at the end of the
      // section's current item list.
      for (let s = 0; s < tpl.sections.length; s++) {
        const sec = tpl.sections[s]!
        let sectionId: string

        const match = existingSections.find(
          (x) => x.title.toLowerCase() === sec.title.toLowerCase()
        )
        if (match) {
          sectionId = match.id
          // We need the current item count so appended items get correct
          // positions. Re-fetch the template to be safe (cheap; one call
          // per matched section, only on append paths).
          try {
            const full = await api.checklists.get(templateId)
            existingItemCount = full.sections.find((x) => x.id === sectionId)?.items.length ?? 0
          } catch {
            existingItemCount = 0
          }
        } else {
          try {
            const created = await api.checklists.createSection(templateId, {
              title: sec.title,
              position: existingSections.length + s,
            })
            sectionId = created.id
            createdSections++
            existingItemCount = 0
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Unknown error'
            runErrors.push({ row: 0, field: 'section', value: `${tpl.name} → ${sec.title}`,
              message: `Failed to create section: ${msg}` })
            continue
          }
        }

        for (let i = 0; i < sec.items.length; i++) {
          const item = sec.items[i]!
          try {
            await api.checklists.createItem(templateId, sectionId, {
              type:        item.type,
              label:       item.label,
              description: item.description,
              required:    item.required,
              position:    existingItemCount + i,
              options:     item.options,
              weight:         item.weight,
              correct_answer: item.correctAnswer,
              option_points:  item.optionPoints,
              min_value:      item.minValue,
              max_value:      item.maxValue,
            })
            createdItems++
          } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Unknown error'
            runErrors.push({ row: item.rowNum, field: 'item', value: item.label,
              message: `Failed to create item: ${msg}` })
          }
        }
      }
    }

    setProgress(null)
    setErrors((prev) => [...prev, ...runErrors])
    setResult({
      createdTemplates, appendedTemplates, newVersions,
      sections: createdSections, items: createdItems, failedTemplates,
    })
    setImporting(false)
    if (createdTemplates + appendedTemplates + newVersions > 0) {
      qc.invalidateQueries({ queryKey: ['checklists'] })
      const verb = createdTemplates > 0 && appendedTemplates + newVersions > 0
        ? 'imported / updated'
        : createdTemplates > 0 ? 'imported' : 'updated'
      toast.success(`${createdTemplates + appendedTemplates + newVersions} checklist${createdTemplates + appendedTemplates + newVersions === 1 ? '' : 's'} ${verb}`)
    }
  }

  function reset() {
    setFile(null)
    setParsed(null)
    setErrors([])
    setResult(null)
    setProgress(null)
    setExisting([])
    if (fileRef.current) fileRef.current.value = ''
  }

  function handleClose() {
    reset()
    onClose()
  }

  const hasValidationErrors = errors.length > 0 && !result
  const allValid = parsed && parsed.length > 0 && errors.length === 0 && !result
  const totalItems    = parsed?.reduce((acc, t) => acc + t.sections.reduce((s, sec) => s + sec.items.length, 0), 0) ?? 0
  const totalSections = parsed?.reduce((acc, t) => acc + t.sections.length, 0) ?? 0

  // Per-template plan, for the summary card. Only meaningful when
  // appendMode is on; otherwise everything's a fresh create.
  const plans = parsed && appendMode
    ? parsed.map((t) => planForTemplate(t, existing))
    : null
  const planCounts = plans
    ? {
        create:       plans.filter((p) => p.mode === 'create').length,
        appendDraft:  plans.filter((p) => p.mode === 'append-draft').length,
        newVersion:   plans.filter((p) => p.mode === 'append-via-new-version').length,
      }
    : null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Import checklists</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
          Upload a CSV file to create one or more checklist templates at once. Each row is one item;
          rows are grouped into sections by <code>section_title</code> and into templates by
          <code> template_name</code>. Imported templates land as <strong>drafts</strong> — review and
          publish them from the checklist detail page.
        </Typography>

        {/* Download template */}
        <Box
          onClick={downloadSample}
          sx={{
            px: 2.5, py: 2, mb: 2.5, borderRadius: 2,
            border: '1px solid', borderColor: 'divider', cursor: 'pointer',
            transition: 'border-color 120ms ease',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Download template CSV</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                13 columns (incl. scoring), sample showing 2 templates with multiple sections + item types
              </Typography>
            </Box>
            <Chip label=".csv" size="small"
              sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary' }} />
          </Stack>
        </Box>

        <Divider sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Upload
          </Typography>
        </Divider>

        <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />

        {!file ? (
          <Box
            onClick={() => fileRef.current?.click()}
            sx={{
              py: 5, textAlign: 'center', borderRadius: 2,
              border: '2px dashed', borderColor: 'divider', cursor: 'pointer',
              transition: 'border-color 120ms ease, background-color 120ms ease',
              '&:hover': { borderColor: 'primary.main', bgcolor: primary.hover },
            }}
          >
            <FileUploadOutlinedIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.6, mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Click to upload CSV</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>or drag and drop</Typography>
          </Box>
        ) : (
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 2, py: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{file.name}</Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                  {parsed
                    ? `${parsed.length} template${parsed.length === 1 ? '' : 's'} · ${totalSections} section${totalSections === 1 ? '' : 's'} · ${totalItems} item${totalItems === 1 ? '' : 's'}`
                    : 'Parsing…'}
                </Typography>
              </Box>
              <Button size="small" onClick={reset} disabled={importing}
                sx={{ fontSize: 12, color: 'text.secondary' }}>Change</Button>
            </Stack>

            {/* Append-mode toggle — only meaningful before a result is shown */}
            {!result && (
              <Stack direction="row" alignItems="center" spacing={2}
                sx={{ px: 2, py: 1.5, borderRadius: 1.5, mb: 2,
                  border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Append to existing checklists
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25, lineHeight: 1.5 }}>
                    On: rows for a template name that already exists are appended to it
                    (drafts edited in place; published checklists get a new draft version).
                    Off: every row creates a brand-new template, even if the name matches.
                  </Typography>
                </Box>
                <Switch
                  checked={appendMode}
                  disabled={importing}
                  onChange={(e) => setAppendMode(e.target.checked)}
                />
              </Stack>
            )}

            {hasValidationErrors && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
                    {errors.length} issue{errors.length === 1 ? '' : 's'} found — fix before importing
                  </Typography>
                </Stack>
                <Box sx={{ maxHeight: 240, overflowY: 'auto', borderRadius: 1.5,
                  border: '1px solid', borderColor: 'rgba(220,38,38,0.2)',
                  bgcolor: 'rgba(220,38,38,0.04)' }}>
                  {errors.map((err, i) => (
                    <Stack key={i} direction="row" spacing={1.5}
                      sx={{ px: 2, py: 1.25,
                        borderBottom: i < errors.length - 1 ? '1px solid' : 'none',
                        borderColor: 'rgba(220,38,38,0.12)' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#DC2626',
                        fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        {err.row > 0 ? `Row ${err.row}` : '—'}
                      </Typography>
                      <Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                          {err.field}: <Typography component="span"
                            sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>"{err.value}"</Typography>
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{err.message}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Box>
              </Box>
            )}

            {allValid && (
              <Stack direction="row" alignItems="flex-start" spacing={1}
                sx={{ px: 2, py: 1.5, borderRadius: 1.5,
                  bgcolor: 'rgba(5,150,105,0.06)',
                  border: '1px solid', borderColor: 'rgba(5,150,105,0.2)', mb: 2 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#059669', mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>
                    {parsed.length} template{parsed.length === 1 ? '' : 's'} ready to import
                  </Typography>
                  {planCounts && (planCounts.appendDraft + planCounts.newVersion > 0) && (
                    <Typography sx={{ fontSize: 11.5, color: '#065F46', mt: 0.5, lineHeight: 1.6 }}>
                      {planCounts.create > 0       && <>{planCounts.create} new · </>}
                      {planCounts.appendDraft > 0  && <>{planCounts.appendDraft} appending to existing draft{planCounts.appendDraft === 1 ? '' : 's'} · </>}
                      {planCounts.newVersion > 0   && <>{planCounts.newVersion} creating new draft version{planCounts.newVersion === 1 ? '' : 's'} of published checklist{planCounts.newVersion === 1 ? '' : 's'}</>}
                    </Typography>
                  )}
                </Box>
              </Stack>
            )}

            {progress && (
              <Stack direction="row" alignItems="center" spacing={1.5}
                sx={{ px: 2, py: 1.5, borderRadius: 1.5, mb: 2,
                  bgcolor: (t) => `${t.palette.primary.main}10`,
                  border: '1px solid', borderColor: 'primary.main' }}>
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main' }}>
                  Creating {progress.template} of {progress.total}: {progress.name}
                </Typography>
              </Stack>
            )}

            {result && (
              <Box sx={{ mb: 2 }}>
                <Stack spacing={1}>
                  {(result.createdTemplates + result.appendedTemplates + result.newVersions) > 0 && (
                    <Stack direction="row" alignItems="flex-start" spacing={1}
                      sx={{ px: 2, py: 1.25, borderRadius: 1.5,
                        bgcolor: 'rgba(5,150,105,0.06)',
                        border: '1px solid', borderColor: 'rgba(5,150,105,0.2)' }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#059669', mt: 0.25 }} />
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>
                          {result.sections} section{result.sections === 1 ? '' : 's'},{' '}
                          {result.items} item{result.items === 1 ? '' : 's'} written
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#065F46', mt: 0.5, lineHeight: 1.6 }}>
                          {result.createdTemplates  > 0 && <>{result.createdTemplates} new template{result.createdTemplates === 1 ? '' : 's'}{(result.appendedTemplates + result.newVersions) > 0 ? ' · ' : ''}</>}
                          {result.appendedTemplates > 0 && <>{result.appendedTemplates} appended to existing draft{result.appendedTemplates === 1 ? '' : 's'}{result.newVersions > 0 ? ' · ' : ''}</>}
                          {result.newVersions       > 0 && <>{result.newVersions} new draft version{result.newVersions === 1 ? '' : 's'} (publish to make live)</>}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                  {result.failedTemplates > 0 && (
                    <Stack direction="row" alignItems="center" spacing={1}
                      sx={{ px: 2, py: 1, borderRadius: 1.5,
                        bgcolor: 'rgba(220,38,38,0.06)',
                        border: '1px solid', borderColor: 'rgba(220,38,38,0.2)' }}>
                      <ErrorOutlineIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
                        {result.failedTemplates} template{result.failedTemplates === 1 ? '' : 's'} failed — see errors above
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* Format guide */}
        <Box sx={{ mt: 2.5, px: 2.5, py: 2, borderRadius: 2,
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
          border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
            CSV columns
          </Typography>
          <Stack spacing={0.75}>
            <FormatRow field="template_name" required desc="Groups rows into one template" />
            <FormatRow field="template_description" desc="Read from the first row of each template; ignored on later rows" />
            <FormatRow field="section_title" required desc="Groups items into sections (in order of first appearance)" />
            <FormatRow field="item_label" required desc="The question / instruction text" />
            <FormatRow field="item_type" required desc={VALID_TYPES.join(' | ')} />
            <FormatRow field="required" desc="true | false (default false)" />
            <FormatRow field="item_description" desc="Helper text under the label" />
            <FormatRow field="options" desc="Pipe-separated (Clean|Dirty|Replace) — required for multiple_choice and checkbox" />
            <FormatRow field="weight" desc="Max points this question is worth. Blank/0 = not scored." />
            <FormatRow field="correct_answer" desc="yes_no: yes|no — pass_fail: pass|fail. The answer that earns full weight." />
            <FormatRow field="option_points" desc="Pipe-separated points matching options in order (e.g. 10|5|0|0) — multiple_choice/checkbox only" />
            <FormatRow field="min_value" desc="Acceptable range floor — number items only" />
            <FormatRow field="max_value" desc="Acceptable range ceiling — number items only" />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={importing}
          sx={{ fontSize: 13, color: 'text.secondary' }}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button variant="contained"
            onClick={handleImport}
            disabled={!allValid || importing}
            startIcon={importing ? <CircularProgress size={14} /> : <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ fontSize: 13, fontWeight: 600 }}>
            {importing
              ? 'Importing…'
              : `Import ${parsed?.length ?? 0} template${(parsed?.length ?? 0) === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

// ─── Append-mode planning ──────────────────────────────────────────────────
//
// Given a parsed template + the workspace's current checklist list,
// decide what to do:
//
//   create                  no name match → fresh template
//   append-draft            match exists and is a draft → mutate in place
//   append-via-new-version  match exists and is published → call
//                           newVersion to clone into a draft, then mutate.
//                           The published version stays live until the
//                           user publishes the new draft.
//
// Archived matches are deliberately treated as no-match (we don't
// touch archived rows — feels surprising to the user).
type TemplatePlan =
  | { mode: 'create' }
  | { mode: 'append-draft'; existingId: string }
  | { mode: 'append-via-new-version'; existingId: string }

function planForTemplate(
  tpl: ParsedTemplate, existing: ChecklistTemplateSummary[]
): TemplatePlan {
  const target = nameMatch(tpl.name, existing)
  if (!target) return { mode: 'create' }
  if (target.status === 'draft')     return { mode: 'append-draft', existingId: target.id }
  if (target.status === 'published') return { mode: 'append-via-new-version', existingId: target.id }
  return { mode: 'create' } // archived → fresh draft
}

function nameMatch(name: string, existing: ChecklistTemplateSummary[]) {
  const lc = name.toLowerCase()
  // Only consider current versions so we don't try to append to an old
  // version of a checklist that's been bumped.
  return existing.find((t) => t.is_current && t.name.toLowerCase() === lc) ?? null
}

function FormatRow({ field, required, desc }: { field: string; required?: boolean; desc: string }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: 'ui-monospace, monospace',
        color: 'text.primary', minWidth: 180, whiteSpace: 'nowrap' }}>
        {field}{required && <Typography component="span" sx={{ color: '#DC2626' }}>*</Typography>}
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{desc}</Typography>
    </Stack>
  )
}

// ─── CSV parser ────────────────────────────────────────────────────────────
//
// Each row = one item. Rows are grouped:
//   - by template_name → ParsedTemplate
//   - within a template, by section_title → ParsedSection (order preserved
//     from first row a section appears in)
// Validation catches: missing required columns, missing required cells,
// invalid item_type, missing options on multiple_choice/checkbox, and
// rows that change template_description partway through a template.

function parseCSV(text: string): { templates: ParsedTemplate[]; errors: ImportError[] } {
  const lines = text.replace(/\r\n/g, '\n').trim().split('\n').map((l) => l.trim()).filter(Boolean)
  if (lines.length < 2) {
    return { templates: [], errors: [{ row: 1, field: 'file', value: '',
      message: 'CSV must have a header row and at least one data row.' }] }
  }

  const header = lines[0]!.split(',').map((h) =>
    h.trim().toLowerCase().replace(/^"/, '').replace(/"$/, '')
  )
  const idx = (col: string) => header.indexOf(col)

  const tplNameIdx  = idx('template_name')
  const tplDescIdx  = idx('template_description')
  const secIdx      = idx('section_title')
  const labelIdx    = idx('item_label')
  const typeIdx     = idx('item_type')
  const reqIdx      = idx('required')
  const itemDescIdx = idx('item_description')
  const optsIdx     = idx('options')
  const weightIdx   = idx('weight')
  const correctIdx  = idx('correct_answer')
  const optPtsIdx   = idx('option_points')
  const minIdx      = idx('min_value')
  const maxIdx      = idx('max_value')

  const errors: ImportError[] = []
  const required = [
    ['template_name', tplNameIdx],
    ['section_title', secIdx],
    ['item_label', labelIdx],
    ['item_type', typeIdx],
  ] as const
  for (const [col, i] of required) {
    if (i < 0) {
      errors.push({ row: 1, field: 'header', value: header.join(', '),
        message: `Missing required column "${col}".` })
    }
  }
  if (errors.length > 0) return { templates: [], errors }

  // Use ordered Maps so the import flow preserves the order rows
  // appeared in the file.
  const templates = new Map<string, ParsedTemplate>()

  for (let i = 1; i < lines.length; i++) {
    const cols  = parseCSVLine(lines[i]!)
    const get   = (j: number) => (j < 0 ? '' : (cols[j] ?? '').trim())
    const rowNum = i + 1

    const tplName = get(tplNameIdx)
    const secTitle = get(secIdx)
    const label   = get(labelIdx)
    const typeRaw = get(typeIdx).toLowerCase()

    if (!tplName) {
      errors.push({ row: rowNum, field: 'template_name', value: '', message: 'Template name is required.' })
      continue
    }
    if (!secTitle) {
      errors.push({ row: rowNum, field: 'section_title', value: '', message: 'Section title is required.' })
      continue
    }
    if (!label) {
      errors.push({ row: rowNum, field: 'item_label', value: '', message: 'Item label is required.' })
      continue
    }
    if (!typeRaw) {
      errors.push({ row: rowNum, field: 'item_type', value: '', message: 'Item type is required.' })
      continue
    }
    if (!VALID_TYPES.includes(typeRaw as ChecklistItemType)) {
      errors.push({ row: rowNum, field: 'item_type', value: typeRaw,
        message: `Invalid type. Must be one of: ${VALID_TYPES.join(', ')}` })
      continue
    }
    const type = typeRaw as ChecklistItemType

    const required = parseBool(get(reqIdx))
    const itemDesc = get(itemDescIdx)
    const optsRaw  = get(optsIdx)
    const options  = optsRaw
      ? optsRaw.split('|').map((s) => s.trim()).filter(Boolean)
      : undefined

    if (TYPES_REQUIRING_OPTIONS.includes(type) && (!options || options.length < 2)) {
      errors.push({ row: rowNum, field: 'options', value: optsRaw,
        message: `"${type}" items need at least 2 pipe-separated options (e.g. Yes|No|N/A).` })
      continue
    }
    if (options && !TYPES_REQUIRING_OPTIONS.includes(type)) {
      // Silently ignore stray options on item types that don't accept
      // them — saves the user from having to clean blank cells.
    }

    // Scoring — all optional. A blank/zero weight means "not scored",
    // same default as the template builder.
    const weightRaw = get(weightIdx)
    const weight = weightRaw ? Number(weightRaw) : undefined
    if (weightRaw && (Number.isNaN(weight) || (weight as number) < 0)) {
      errors.push({ row: rowNum, field: 'weight', value: weightRaw, message: 'Weight must be a non-negative number.' })
      continue
    }

    const correctRaw = get(correctIdx).toLowerCase()
    let correctAnswer: string | undefined
    if (correctRaw) {
      if (type !== 'yes_no' && type !== 'pass_fail') {
        errors.push({ row: rowNum, field: 'correct_answer', value: correctRaw,
          message: '"correct_answer" only applies to yes_no and pass_fail items.' })
        continue
      }
      const validAnswers = type === 'yes_no' ? ['yes', 'no'] : ['pass', 'fail']
      if (!validAnswers.includes(correctRaw)) {
        errors.push({ row: rowNum, field: 'correct_answer', value: correctRaw,
          message: `"correct_answer" for ${type} must be one of: ${validAnswers.join(', ')}` })
        continue
      }
      correctAnswer = correctRaw
    }

    const optPtsRaw = get(optPtsIdx)
    let optionPoints: Record<string, number> | undefined
    if (optPtsRaw) {
      if (!TYPES_REQUIRING_OPTIONS.includes(type) || !options) {
        errors.push({ row: rowNum, field: 'option_points', value: optPtsRaw,
          message: '"option_points" only applies to multiple_choice and checkbox items.' })
        continue
      }
      const pts = optPtsRaw.split('|').map((s) => Number(s.trim()))
      if (pts.length !== options.length || pts.some((p) => Number.isNaN(p))) {
        errors.push({ row: rowNum, field: 'option_points', value: optPtsRaw,
          message: `"option_points" must have exactly ${options.length} pipe-separated number${options.length === 1 ? '' : 's'}, matching "options" in order.` })
        continue
      }
      // No option can be worth more than the question's own weight —
      // same rule the template builder enforces at entry time.
      if (weight != null && pts.some((p) => p > weight!)) {
        errors.push({ row: rowNum, field: 'option_points', value: optPtsRaw,
          message: `No option can be worth more than the question's weight (${weight}).` })
        continue
      }
      optionPoints = Object.fromEntries(options.map((opt, i) => [opt, pts[i]!]))
    }

    const minRaw = get(minIdx)
    const maxRaw = get(maxIdx)
    if ((minRaw || maxRaw) && type !== 'number') {
      errors.push({ row: rowNum, field: 'min_value', value: minRaw || maxRaw,
        message: '"min_value"/"max_value" only apply to number items.' })
      continue
    }
    const minValue = minRaw ? Number(minRaw) : undefined
    const maxValue = maxRaw ? Number(maxRaw) : undefined

    let tpl = templates.get(tplName)
    if (!tpl) {
      tpl = {
        name: tplName,
        description: get(tplDescIdx) || undefined,
        sections: [],
      }
      templates.set(tplName, tpl)
    } else {
      // Warn (not error) if the description disagrees with the first row.
      const desc = get(tplDescIdx)
      if (desc && tpl.description && desc !== tpl.description) {
        errors.push({ row: rowNum, field: 'template_description', value: desc,
          message: `Template "${tplName}" already has description "${tpl.description}" — later rows must leave the column blank or match.` })
        continue
      }
      // First row had blank description, later row has one → adopt it.
      if (desc && !tpl.description) tpl.description = desc
    }

    let sec = tpl.sections.find((s) => s.title === secTitle)
    if (!sec) {
      sec = { title: secTitle, items: [] }
      tpl.sections.push(sec)
    }

    sec.items.push({
      rowNum,
      label,
      type,
      required,
      description: itemDesc || undefined,
      options: TYPES_REQUIRING_OPTIONS.includes(type) ? options : undefined,
      weight, correctAnswer, optionPoints, minValue, maxValue,
    })
  }

  return { templates: [...templates.values()], errors }
}

function parseBool(raw: string): boolean {
  const s = raw.trim().toLowerCase()
  return s === 'true' || s === 'yes' || s === '1' || s === 'y'
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; continue }
      inQuotes = !inQuotes
      continue
    }
    if (ch === ',' && !inQuotes) { result.push(current); current = ''; continue }
    current += ch
  }
  result.push(current)
  return result
}
